import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, query, orderBy, limit, onSnapshot, doc, updateDoc, serverTimestamp } from 'firebase/firestore';

// ---------------------------------------------------------------------------
// Client Dashboard — AI Answers (client profile card)
//
// The questions tracked every month to see whether ChatGPT, Perplexity and
// Google AI recommend this client for its highest-value services (plus the
// Google map top 3 for the same searches). Saved on clients/{id}:
//   dashboardSources.aiQuestions  [{ text, service }]  (up to 8)
//   dashboardSources.aiLocation   "City,State,United States" — where the Google
//                                 searches come from
//   dashboardSources.aiEnabled    false switches the check off
// "Suggest with AI" asks Claude (dashboardAiDraft) for questions from the brand
// kit and the client's search demand; nothing saves until Save questions.
// Changing a question's wording starts a fresh trend for that question.
// The check runs on the 4th for the month just ended (functions-dashboard,
// lib/aiAnswers.js); the latest results show below for staff.
// ---------------------------------------------------------------------------

const BASE = 'https://us-central1-core6-marketing.cloudfunctions.net';
const MAX = 8;
const ENGINES = [['chatgpt', 'ChatGPT'], ['perplexity', 'Perplexity'], ['google_ai_mode', 'Google AI']];

async function api(path, body) {
  const token = await auth.currentUser.getIdToken();
  const r = await fetch(`${BASE}/${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
  return data;
}

const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '7px 9px', fontSize: 13,
  border: '1px solid var(--border-color, #d6d9de)', borderRadius: 6, background: 'var(--bg-primary, #fff)', color: 'inherit',
};
const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '.3px', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 };
const toRows = qs => (Array.isArray(qs) ? qs : []).map(q => (typeof q === 'string' ? { text: q, service: '' } : { text: q.text || '', service: q.service || '' }));

// ✓ / ✗ with words, never colour alone.
function Cell({ r }) {
  if (!r) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  if (r.status !== 'ok') return <span style={{ color: 'var(--text-muted)' }}>{r.status === 'error' ? 'error' : r.status === 'not_shown' ? 'none' : 'no answer'}</span>;
  return r.named ? <strong>✓ #{r.position}</strong> : <span>✗</span>;
}

export default function DashboardAiPanel({ clientId, client }) {
  const saved = client?.dashboardSources || {};
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(toRows(saved.aiQuestions));
  const [location, setLocation] = useState(saved.aiLocation || '');
  const [enabled, setEnabled] = useState(saved.aiEnabled !== false);
  const [why, setWhy] = useState({});
  const [latest, setLatest] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    const s = client?.dashboardSources || {};
    setRows(toRows(s.aiQuestions));
    setLocation(s.aiLocation || '');
    setEnabled(s.aiEnabled !== false);
  }, [client?.dashboardSources]);

  useEffect(() => {
    if (!open) return undefined;
    const q = query(collection(db, 'clients', clientId, 'aiMonths'), orderBy('month', 'desc'), limit(1));
    return onSnapshot(q, snap => setLatest(snap.docs[0] ? snap.docs[0].data() : null), () => setLatest(null));
  }, [open, clientId]);

  const setRow = (i, key, value) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, [key]: value } : r)));
  const removeRow = i => setRows(rs => rs.filter((_, j) => j !== i));

  const suggest = async () => {
    setBusy('suggest'); setMsg(null);
    try {
      const d = await api('dashboardAiDraft', { viewAs: clientId, count: Math.max(5, rows.filter(r => r.text.trim()).length) });
      setRows(d.questions.map(q => ({ text: q.text, service: q.service || '' })));
      setWhy(Object.fromEntries(d.questions.map(q => [q.text, q.why])));
      if (d.location && !location.trim()) setLocation(d.location);
      setMsg({ ok: true, text: `Suggested ${d.questions.length} questions${d.location ? ` (search location ${d.location})` : ''}. Review, edit, then Save questions.` });
    } catch (err) { setMsg({ ok: false, text: err.message }); }
    finally { setBusy(null); }
  };

  const save = async () => {
    setBusy('save'); setMsg(null);
    try {
      const clean = rows.map(r => ({ text: r.text.replace(/\s+/g, ' ').trim(), service: r.service.trim() || null })).filter(r => r.text).slice(0, MAX);
      const loc = location.split(',').map(x => x.trim()).filter(Boolean).join(',');
      if (clean.length && !loc) throw new Error('Add the search location, e.g. Watsonville,California,United States');
      await updateDoc(doc(db, 'clients', clientId), {
        'dashboardSources.aiQuestions': clean,
        'dashboardSources.aiLocation': loc || null,
        'dashboardSources.aiEnabled': enabled,
        'dashboardSources.aiQuestionsSource': 'staff',
        'dashboardSources.aiQuestionsUpdatedAt': serverTimestamp(),
        'dashboardSources.aiQuestionsUpdatedBy': auth.currentUser?.email || null,
      });
      setMsg({ ok: true, text: 'Saved. The next monthly check (on the 4th) uses these questions.' });
    } catch (err) { setMsg({ ok: false, text: err.message }); }
    finally { setBusy(null); }
  };

  const count = (saved.aiQuestions || []).length;
  const totals = latest ? latest.questions.reduce((t, q) => {
    for (const [k] of ENGINES) { const r = q.engines && q.engines[k]; if (r && r.status === 'ok') { t.asked++; if (r.named) t.named++; } }
    if (q.map && q.map.status === 'ok') { t.searches++; if (q.map.position > 0) t.pack++; }
    return t;
  }, { asked: 0, named: 0, searches: 0, pack: 0 }) : null;

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Client Dashboard AI Answers</div>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
            {saved.aiEnabled === false ? 'OFF · ' : 'ON · '}{count} question{count === 1 ? '' : 's'}
          </span>
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{open ? '▾ Hide' : '▸ Show'}</span>
      </div>

      {open && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 14 }}>
            Questions a local customer would ask an AI assistant about this client's highest-value services. Each month every question goes to
            ChatGPT, Perplexity and Google AI (and a Google search from the location below for the map top 3); the dashboard shows how often the
            client is recommended and who is recommended instead. Rewording a question starts a fresh trend for it.
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, marginBottom: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
            Run the monthly AI Answers check for this client
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr auto', gap: 8, marginBottom: 6 }}>
            <div style={labelStyle}>Question</div><div style={labelStyle}>Service</div><div />
          </div>
          {rows.map((r, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr auto', gap: 8, alignItems: 'center' }}>
                <input style={inputStyle} value={r.text} maxLength={200} onChange={e => setRow(i, 'text', e.target.value)} placeholder="e.g. Who can repair a sewer line in Watsonville, CA?" />
                <input style={inputStyle} value={r.service} maxLength={80} onChange={e => setRow(i, 'service', e.target.value)} placeholder="Sewer line repair" />
                <button type="button" className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => removeRow(i)} title="Remove question">✕</button>
              </div>
              {why[r.text] && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>Why: {why[r.text]}</div>}
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-secondary" style={{ fontSize: 12 }} disabled={rows.length >= MAX} onClick={() => setRows(rs => [...rs, { text: '', service: '' }])}>+ Add question</button>
            <button type="button" className="btn btn-secondary" style={{ fontSize: 12 }} disabled={!!busy} onClick={suggest}>{busy === 'suggest' ? 'Asking Claude…' : 'Suggest with AI'}</button>
          </div>

          <div style={{ maxWidth: 420, marginBottom: 14 }}>
            <label style={labelStyle}>Search location for Google</label>
            <input style={inputStyle} value={location} onChange={e => setLocation(e.target.value)} placeholder="Watsonville,California,United States" />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>City,State,United States. If Google doesn't know the city, the state is used.</div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
            <button type="button" className="btn btn-primary" disabled={!!busy} onClick={save}>{busy === 'save' ? 'Saving…' : 'Save questions'}</button>
            {msg && <span style={{ fontSize: 12, fontWeight: 600 }}>{msg.ok ? '✓ ' : '✕ '}{msg.text}</span>}
          </div>

          <div style={labelStyle}>Latest check</div>
          {!latest && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No check yet. The first runs on the 4th of next month.</div>}
          {latest && (
            <>
              <div style={{ fontSize: 13, marginBottom: 8 }}>
                <strong>{latest.month}</strong> · checked {latest.checkedOn || '—'} from {latest.location} · recommended in <strong>{totals.named} of {totals.asked}</strong> AI answers ·
                map top 3 in <strong>{totals.pack} of {totals.searches}</strong> searches{latest.cost != null ? ` · cost $${latest.cost}` : ''}
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 640 }}>
                  <thead><tr>{['Question', ...ENGINES.map(e => e[1]), 'AI Overview', 'Map top 3', 'Recommended instead'].map(h => <th key={h} style={{ textAlign: 'left', padding: '6px 8px 6px 0', borderBottom: '1px solid var(--border-color, #e3e6ea)', color: 'var(--text-muted)' }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {latest.questions.map(q => (
                      <tr key={q.text}>
                        <td style={{ padding: '6px 8px 6px 0', borderBottom: '1px solid var(--border-color, #eef0f3)' }}>{q.text}</td>
                        {ENGINES.map(([k]) => <td key={k} style={{ padding: '6px 8px 6px 0', borderBottom: '1px solid var(--border-color, #eef0f3)' }}><Cell r={q.engines && q.engines[k]} /></td>)}
                        <td style={{ padding: '6px 8px 6px 0', borderBottom: '1px solid var(--border-color, #eef0f3)' }}><Cell r={q.engines && q.engines.google_ai_overview} /></td>
                        <td style={{ padding: '6px 8px 6px 0', borderBottom: '1px solid var(--border-color, #eef0f3)' }}>{q.map && q.map.status === 'ok' ? (q.map.position > 0 ? <strong>✓ #{q.map.position}</strong> : '✗') : '—'}</td>
                        <td style={{ padding: '6px 8px 6px 0', borderBottom: '1px solid var(--border-color, #eef0f3)', color: 'var(--text-muted)' }}>
                          {[...new Set(ENGINES.flatMap(([k]) => (q.engines && q.engines[k] && q.engines[k].businesses) || []))].slice(0, 4).join(', ') || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
